import type { MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type IconProps = {
  size?: number;
  className?: string;
};

/** Collapse/expand chevron (points down; rotates up when expanded). */
export function IconChevron({ size = 14, expanded = false, className = '' }: IconProps & { expanded?: boolean }) {
  return (
    <span
      className={`ccr-icon-chevron${expanded ? ' is-expanded' : ''}${className ? ` ${className}` : ''}`}
      aria-hidden
    >
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </span>
  );
}

/** Yellow triangle warning with exclamation (under-16 / review badges). */
export function IconWarningTriangle({ size = 14, className = '' }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className || undefined}
      aria-hidden
    >
      <path
        className="warn-tri"
        d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
      />
      <line className="warn-mark" x1="12" y1="9" x2="12" y2="13" />
      <circle className="warn-dot" cx="12" cy="17" r="1" />
    </svg>
  );
}

/** Pencil icon for Edit actions. */
export function IconPencil({ size = 13, className = '' }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={className || undefined}
      aria-hidden
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
    </svg>
  );
}

/** Standard Edit link/button styling used across wizard and review. */
export function EditLink({
  to,
  className = '',
  onClick,
}: {
  to: string;
  className?: string;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
}) {
  const { t } = useTranslation();
  return (
    <Link to={to} className={`ccr-edit-link${className ? ` ${className}` : ''}`} onClick={onClick}>
      <IconPencil />
      {t('wizard.edit')}
    </Link>
  );
}
